import { validatePhoneNumberLength } from "libphonenumber-js";
import isCreditCard from "validator/lib/isCreditCard";
import { extend, configure } from "vee-validate";
import {
  email,
  numeric,
  min,
  max,
  alpha_spaces,
  alpha,
  required,
  between,
  regex,
  max_value,
  min_value,
} from "vee-validate/dist/rules";
import { ValidationRule } from "vee-validate/dist/types/types";
import { $t } from "@/lib/i18nHelpers";
import i18n from "@/modules/i18n";

export default {
  install() {
    configure({
      mode: "eager",
      classes: {
        invalid: "__invalid",
      },
      defaultMessage: (field, values) => {
        if (values) values._field_ = i18n.t(`${field}`);
        return i18n.t(`validation.${values?._rule_}`, values) as string;
      },
    });

    const credit_card: ValidationRule = {
      validate: val => isCreditCard(val),
      message: $t("The Card Number field must be a valid credit card number"),
    };

    const credit_card_expire: ValidationRule = {
      validate: val => val.replace(/\/|_/g, "").length === 4,
      message: $t("The Expiration Date field must be a valid date"),
    };

    const no_past_date: ValidationRule = {
      validate: val => new Date(val) >= new Date(),
      message: $t("Date should be in the future"),
    };

    // the browser does not hold the min and max of a date input while the year is being typed, e.g. 0202-02-02
    const date_between: ValidationRule = {
      params: ["min", "max"],
      validate: (val, { min, max }: Record<string, any>) => {
        const time = new Date(val).getTime();
        return time >= new Date(min).getTime() && time <= new Date(max).getTime();
      },
      message: $t("Please enter a valid date"),
    };

    const phone_length: ValidationRule = {
      validate: val => validatePhoneNumberLength(val) === undefined,
      message: (field, params) => {
        const validateResult = validatePhoneNumberLength(params._value_);
        if (validateResult === "TOO_SHORT") {
          return $t("The number you entered is too short");
        } else if (validateResult === "TOO_LONG") {
          return $t("The number you entered is too long");
        } else return $t("The number you entered is invalid");
      },
    };

    const required_select: ValidationRule = {
      validate: val => Number(val) > -1,
    };

    // rules doc: https://logaretm.github.io/vee-validate/guide/rules.html#rules
    const rules: Record<string, ValidationRule> = {
      alpha_spaces,
      alpha,
      required,
      email,
      numeric,
      min,
      max,
      between,
      regex,
      max_value,
      min_value,
      credit_card,
      credit_card_expire,
      required_select,
      phone_length,
      no_past_date,
      date_between,
    };

    Object.keys(rules).forEach(ruleName => {
      const key = ruleName as keyof typeof rules;
      extend(key, rules[key]);
    });
  },
};
